/**
 * The stylizer's controls, and the pieces they are made of.
 *
 * Three shapes and nothing else: a **row**, which is a labelled strip of controls
 * that scrolls sideways so a phone with the keyboard up can still reach every one
 * of them; a **chip**, which is a pressable with words or a glyph on it; and a
 * **swatch**, which is a chip whose face is a colour.
 *
 * They live apart from the panel that decides *what pressing one means*
 * (`DesignToolbar`), so a change to how the toolbar looks never touches a rule
 * about what a card is.
 */
import {type ReactNode} from 'react'
import {
  Pressable,
  ScrollView,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native'

import {Text} from '#/components/Typography'
import {atoms as a, useTheme} from '#/alf'

export function StylizerRow({
  label,
  children,
}: {
  /** What this row's controls are all for, in the app's own words. */
  label: string
  children: ReactNode
}) {
  const t = useTheme()
  return (
    <View style={[a.mt_sm]}>
      <Text
        style={[
          a.text_xs,
          a.font_semi_bold,
          t.atoms.text_contrast_medium,
          {paddingHorizontal: 14, paddingBottom: 6},
        ]}>
        {label}
      </Text>
      <ScrollView
        horizontal
        bounces={false}
        keyboardShouldPersistTaps="always"
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[
          a.flex_row,
          a.align_center,
          a.gap_xs,
          {paddingHorizontal: 14},
        ]}>
        {children}
      </ScrollView>
    </View>
  )
}

export function StylizerChip({
  label,
  onPress,
  selected,
  style,
  testID,
  children,
}: {
  /** The words on the chip, and — when there are no `children` — what it says. */
  label: string
  onPress: () => void
  selected?: boolean
  style?: StyleProp<ViewStyle>
  testID?: string
  children?: ReactNode
}) {
  const t = useTheme()
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{selected: !!selected}}
      onPress={onPress}
      style={({pressed}) => [
        styles.chip,
        t.atoms.bg_contrast_25,
        selected
          ? {borderColor: t.palette.primary_500}
          : t.atoms.border_contrast_low,
        pressed && {opacity: 0.6},
        style,
      ]}>
      {children ?? <Text style={[a.text_sm, t.atoms.text]}>{label}</Text>}
    </Pressable>
  )
}

export function StylizerSwatch({
  color,
  label,
  onPress,
  selected,
  round,
  testID,
}: {
  color: string
  label: string
  onPress: () => void
  selected?: boolean
  /** Ink is drawn as a circle and a background as a square, so the two palettes
   * read as two different questions in one row. */
  round?: boolean
  testID?: string
}) {
  const t = useTheme()
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{selected: !!selected}}
      onPress={onPress}
      style={({pressed}) => [
        styles.swatch,
        round && a.rounded_full,
        {backgroundColor: color},
        selected
          ? {borderColor: t.palette.primary_500}
          : t.atoms.border_contrast_low,
        pressed && {opacity: 0.6},
      ]}
    />
  )
}

const styles = {
  chip: {
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  swatch: {
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 3,
  } satisfies ViewStyle,
}
